// IPD Patient Register — the /admitted_discharged_patients page, rebuilt as a
// sidebar shell (Allocated / Admitted / Discharged / Death Register) instead of
// the original two-tab list (Task C18).
//
// Sidebar + internal view state, no per-view App.js routes — the same shape as
// NurseDashboardShell (Gate-1 ADR), and mounted the same way (inside
// DoctorLayout).
//
// Every view is fed from ONE load: the three admission statuses plus the
// deceased DischargeProcess rows are fetched together, so switching views is
// instant and never re-fetches. Outcome lives only on DischargeProcess, never
// on the admission, so the Death Register is a client-side join by
// admission_id — the same pattern IPDBillingDischarge.js uses for its
// billing-queue join.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import HotelIcon from "@mui/icons-material/Hotel";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LogoutIcon from "@mui/icons-material/Logout";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import { toast } from "react-toastify";

import { nurseTheme, NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { listAdmissions } from "../../api/patientListApi";
import { listDischargeProcesses } from "../../api/dischargeProcessApi";
import PatientListView from "./PatientListView";
import DeathRegisterView from "./DeathRegisterView";
import { normalizeAdmissionRow } from "./registerUtils";

const SIDEBAR_WIDTH = 248;

const NAV_ITEMS = [
  {
    key: "allocated",
    label: "Allocated",
    title: "Allocated Patients",
    icon: <HotelIcon fontSize="small" />,
  },
  {
    key: "admitted",
    label: "Admitted",
    title: "Admitted Patients",
    icon: <LocalHospitalIcon fontSize="small" />,
  },
  {
    key: "discharged",
    label: "Discharged",
    title: "Discharged Patients",
    icon: <LogoutIcon fontSize="small" />,
  },
  {
    key: "deaths",
    label: "Death Register",
    title: "Death Register",
    icon: <HeartBrokenIcon fontSize="small" />,
  },
];

function SidebarContent({ view, onNavigate, counts }) {
  return (
    <Box sx={{ width: SIDEBAR_WIDTH, height: "100%", bgcolor: NURSE_COLORS.primary, color: "#fff", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.25 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "rgba(33,205,192,0.15)", display: "grid", placeItems: "center" }}>
          <AssignmentTurnedInIcon sx={{ color: NURSE_COLORS.accent, fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "#fff", lineHeight: 1.1 }}>
            Patient Register
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
            IPD Census &amp; Registers
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 1, pb: 2 }}>
        <List
          dense
          disablePadding
          subheader={
            <ListSubheader
              disableSticky
              sx={{ bgcolor: "transparent", color: "rgba(255,255,255,0.5)", fontSize: 11, letterSpacing: 0.8, textTransform: "uppercase", lineHeight: 2.2, px: 1.5 }}
            >
              Registers
            </ListSubheader>
          }
        >
          {NAV_ITEMS.map((item) => {
            const active = view === item.key;
            return (
              <ListItemButton
                key={item.key}
                onClick={() => onNavigate(item.key)}
                sx={{
                  borderRadius: 1.5,
                  mb: 0.25,
                  px: 1.5,
                  py: 0.9,
                  color: active ? "#fff" : "rgba(255,255,255,0.78)",
                  bgcolor: active ? "rgba(33,205,192,0.18)" : "transparent",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)" },
                  borderLeft: active ? `3px solid ${NURSE_COLORS.accent}` : "3px solid transparent",
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 32 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ sx: { fontSize: 14, fontWeight: active ? 600 : 500 } }}
                />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  {counts[item.key] ?? 0}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}

export default function IpdPatientRegisterShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [view, setView] = useState("allocated");
  const [allocated, setAllocated] = useState([]);
  const [admitted, setAdmitted] = useState([]);
  const [discharged, setDischarged] = useState([]);
  const [deceasedProcesses, setDeceasedProcesses] = useState([]);
  const [loading, setLoading] = useState(false);

  // allSettled, not all: the four calls are independent, and a single failing
  // one must not blank the other three. Promise.all did exactly that — one 500
  // from discharge-process/ wiped the admission lists too, and the page then
  // read as an honest, empty "No patients found" instead of an error.
  const load = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      listAdmissions("Allocated"),
      listAdmissions("Admitted"),
      listAdmissions("Discharged"),
      listDischargeProcesses({ outcome: "deceased" }),
    ]);

    const failed = results.filter((r) => r.status === "rejected");
    failed.forEach((r) => console.error("Patient register load failed:", r.reason));

    const value = (i) =>
      results[i].status === "fulfilled" ? results[i].value || [] : [];

    setAllocated(value(0).map(normalizeAdmissionRow));
    setAdmitted(value(1).map(normalizeAdmissionRow));
    setDischarged(value(2).map(normalizeAdmissionRow));
    setDeceasedProcesses(value(3));

    if (failed.length) {
      toast.error(
        failed.length === results.length
          ? "Failed to load patient register."
          : "Some registers could not be loaded — showing what we have."
      );
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A death is declared at the Ward stage, long before Physical Discharge closes
  // the admission — so the deceased process must be joined against ALL three
  // admission statuses, not just Discharged. A process whose admission is
  // missing from every list (e.g. a cancelled admission) is dropped rather than
  // rendered as a nameless row.
  const deceasedRows = useMemo(() => {
    const byAdmissionId = new Map();
    [...allocated, ...admitted, ...discharged].forEach((r) => {
      byAdmissionId.set(String(r.admissionId), r);
    });
    return deceasedProcesses
      .map((dp) => {
        const admission = byAdmissionId.get(String(dp.admission_id));
        if (!admission) return null;
        return {
          ...admission,
          dischargeProcessId: dp.id,
          // death_datetime, not advice_datetime — time of death is its own field
          // now (advice_datetime means "tentative discharge advice").
          timeOfDeath: dp.death_datetime || null,
          causeOfDeath: dp.cause_of_death || "",
          mannerOfDeath: dp.manner_of_death || "",
          certifyingDoctorId: dp.certifying_doctor_id || "",
          certifyingDoctorName: dp.certifying_doctor_name || "",
          deathCertificateNumber: dp.death_certificate_number || "",
          // The whole DischargeProcess row — the MCCD editor and the Form 4 /
          // relative-slip PDFs read the structured cause + certifier fields off it.
          raw: dp,
        };
      })
      .filter(Boolean);
  }, [deceasedProcesses, allocated, admitted, discharged]);

  const counts = {
    allocated: allocated.length,
    admitted: admitted.length,
    discharged: discharged.length,
    deaths: deceasedRows.length,
  };

  const navigate = (next) => {
    setView(next);
    setDrawerOpen(false);
  };

  const activeItem = NAV_ITEMS.find((i) => i.key === view) || NAV_ITEMS[0];
  const listRows = { allocated, admitted, discharged }[view];

  return (
    <ThemeProvider theme={nurseTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: "100vh", bgcolor: NURSE_COLORS.pageBg, display: "flex" }}>
        {isDesktop ? (
          <Box component="nav" sx={{ width: SIDEBAR_WIDTH, flexShrink: 0, position: "sticky", top: 0, height: "100vh" }}>
            <SidebarContent view={view} onNavigate={navigate} counts={counts} />
          </Box>
        ) : (
          <Drawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{ sx: { bgcolor: NURSE_COLORS.primary, border: 0 } }}
          >
            <SidebarContent view={view} onNavigate={navigate} counts={counts} />
          </Drawer>
        )}

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          {!isDesktop && (
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: NURSE_COLORS.primary }}>
              <Toolbar sx={{ gap: 1.5, minHeight: 56 }}>
                <IconButton edge="start" sx={{ color: "#fff" }} onClick={() => setDrawerOpen(true)}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ color: "#fff", flex: 1 }} noWrap>
                  Patient Register
                </Typography>
              </Toolbar>
            </AppBar>
          )}

          <Box sx={{ p: { xs: 1.5, md: 3 }, flex: 1, minWidth: 0 }}>
            {view === "deaths" ? (
              <DeathRegisterView rows={deceasedRows} loading={loading} onRefresh={load} />
            ) : (
              <PatientListView
                title={activeItem.title}
                rows={listRows}
                loading={loading}
                onRefresh={load}
                showSummary={view === "discharged"}
              />
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
